import Seo from "@/components/Seo";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { REACH_STATS } from "@/lib/content";
import { CASE_STUDIES } from "@/lib/caseStudies";
import { ArrowUpRight, Mail, Newspaper, Linkedin, Globe, ArrowDown } from "lucide-react";

const CHANNELS = [
  {
    icon: Globe,
    title: "HighwaysIndustry.com",
    desc: "900,000+ annual pageviews (74,000 monthly average) across the UK highways sector - operatives, engineers, directors and procurement.",
  },
  {
    icon: Mail,
    title: "Newsletter",
    desc: "16,000 named subscribers. Sent 3× weekly to senior decision-makers across highways and infrastructure.",
  },
  {
    icon: Linkedin,
    title: "Social Network",
    desc: "300,000+ followers across our LinkedIn, Facebook and industry channels.",
  },
  {
    icon: Newspaper,
    title: "Industry Audience",
    desc: "Named lists of framework holders, Tier 1 contractors, asset owners and policy bodies.",
  },
];

export default function IndustryReach() {
  return (
    <main data-testid="industry-reach-page" className="bg-zinc-950">
      <Seo
        title="Industry reach"
        description="Millions of highways and infrastructure professionals reached every month through HighwaysIndustry.com's website, LinkedIn, newsletter and events network. This is where the sector lives."
        path="/industry-reach"
      />
      {/* ─────────────── Showreel Hero ─────────────── */}
      <section
        data-testid="industry-reach-hero"
        className="dark-section relative min-h-[85vh] flex flex-col overflow-hidden bg-black"
      >
        <video
          data-testid="industry-reach-hero-video"
          autoPlay
          muted
          loop
          playsInline
          className="absolute inset-0 w-full h-full object-cover opacity-70"
        >
          <source src="/videos/industry-reach-showreel.mp4" type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-black/55" />
        <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-zinc-950/30 to-transparent" />

        <div className="relative z-10 w-full px-5 md:px-10 pt-28 md:pt-32">
          <div className="mx-auto max-w-[1400px] flex items-start justify-between gap-6">
            <div className="flex items-center gap-3">
              <span className="h-px w-12 bg-accent-blue" />
              <span className="label-eyebrow text-accent-blue">
                Showreel · Industry Reach
              </span>
            </div>
            <span className="hidden md:inline-block font-mono text-[10px] text-accent-blue tracking-widest">
              CREATE · AMPLIFY · POSITION
            </span>
          </div>
        </div>

        <div className="relative z-10 w-full px-5 md:px-10 pb-20 lg:pb-24 mt-auto">
          <div className="mx-auto max-w-[1400px]">
            <motion.h1
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: "easeOut" }}
              className="font-heading font-black text-5xl md:text-7xl lg:text-[7rem] text-white leading-[0.95] tracking-tighter max-w-5xl"
            >
              The biggest <span className="text-accent-blue italic">difference</span>
              <br />
              is who sees it.
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
              className="mt-8 text-lg md:text-xl text-zinc-200 max-w-2xl leading-relaxed"
            >
              A look at the work we make for the UK highways sector, and
              the audience we put it in front of.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.35, ease: "easeOut" }}
              className="mt-10 flex flex-wrap gap-3"
            >
              <Link
                to="/positioning-review"
                data-testid="reach-hero-cta-discovery"
                className="bg-primary-blue text-white px-7 py-4 text-sm font-bold uppercase tracking-widest inline-flex items-center gap-2 hover:glow-blue transition-all rounded-full"
              >
                Book A Discovery Call <ArrowUpRight className="h-4 w-4" />
              </Link>
              <a
                href="#reach-detail"
                onClick={(e) => {
                  e.preventDefault();
                  const el = document.getElementById("reach-detail");
                  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
                }}
                className="text-white/90 hover:text-accent-blue px-5 py-4 text-sm font-bold uppercase tracking-widest border border-white/30 hover:border-accent-blue transition-colors rounded-full inline-flex items-center gap-2"
              >
                See the numbers <ArrowDown className="h-4 w-4" />
              </a>
            </motion.div>
          </div>
        </div>
      </section>

      <section id="reach-detail" className="mx-auto max-w-[1400px] px-5 md:px-10 pt-20 md:pt-28">
        <div className="flex items-center gap-3">
          <span className="h-px w-12 bg-accent-blue" />
          <span className="label-eyebrow text-accent-blue">
            Industry Reach · The Biggest Difference
          </span>
        </div>
        <h1 className="font-heading font-black text-5xl md:text-7xl mt-6 max-w-5xl leading-[0.95] tracking-tighter">
          Most agencies create content.
          <br />
          <span className="text-accent-blue">We create it and distribute it.</span>
        </h1>
        <p className="mt-8 text-lg md:text-xl text-zinc-300 max-w-3xl leading-relaxed">
          We aren&apos;t a marketing agency hoping your content gets seen.
          We&apos;re a publisher with one of the largest dedicated highways
          audiences in the UK, and your content goes straight into it.
        </p>
        <div className="mt-10 flex flex-wrap gap-3">
          <Link
            to="/positioning-review"
            data-testid="reach-cta-discovery"
            className="bg-primary-blue text-white px-7 py-4 text-sm font-bold uppercase tracking-widest inline-flex items-center gap-2 hover:glow-blue transition-all"
          >
            Book A Discovery Call <ArrowUpRight className="h-4 w-4" />
          </Link>
        </div>
      </section>

      {/* Reach metrics */}
      <section
        data-testid="reach-stats"
        className="mt-20 border-y border-zinc-800 py-16 bg-black relative overflow-hidden"
      >
        <div className="absolute inset-0 blueprint-dots opacity-30 pointer-events-none" />
        <div className="relative mx-auto max-w-[1400px] px-5 md:px-10">
          <div className="mb-8 flex items-baseline gap-3 flex-wrap">
            <p className="text-xs font-heading font-semibold uppercase tracking-widest text-accent-blue">
              HighwaysIndustry.com
            </p>
            <span className="text-xs text-zinc-500">
              Established 2011 · 15 years in UK highways
            </span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-px bg-zinc-800 border border-zinc-800">
            {REACH_STATS.map((s) => (
              <div
                key={s.label}
                className="bg-zinc-950 p-6 md:p-10"
                data-testid={`reach-stat-${s.label.replace(/\s+/g, "-").toLowerCase().replace(/[^a-z0-9-]/g, "")}`}
              >
                <p className="text-[10px] font-heading font-semibold uppercase tracking-widest text-zinc-500">
                  {s.label}
                </p>
                <p className="mt-3 font-heading font-black text-4xl md:text-6xl text-accent-blue tracking-tighter leading-none">
                  {s.value}
                </p>
                <p className="mt-3 text-xs md:text-sm text-zinc-400 leading-relaxed">
                  {s.note}
                </p>
              </div>
            ))}
          </div>
          <p className="mt-6 text-[11px] text-zinc-500 leading-relaxed max-w-3xl">
            Figures correct as at July 2026. Sources: Google Analytics 4,
            Google Search Console, platform analytics.
          </p>
        </div>
      </section>

      {/* Channels */}
      <section className="py-20 md:py-28">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10">
          <div className="flex items-end justify-between flex-wrap gap-6">
            <div>
              <div className="flex items-center gap-3">
                <span className="h-px w-12 bg-accent-blue" />
                <span className="label-eyebrow text-accent-blue">
                  The Channels
                </span>
              </div>
              <h2 className="font-heading font-black text-4xl md:text-6xl mt-4 leading-[0.95] tracking-tighter max-w-3xl">
                Where your content goes.
              </h2>
            </div>
          </div>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-px bg-zinc-800 border border-zinc-800">
            {CHANNELS.map((c, i) => {
              const Icon = c.icon;
              return (
                <div
                  key={c.title}
                  data-testid={`reach-channel-${i}`}
                  className="bg-zinc-950 p-8 md:p-10"
                >
                  <Icon className="h-7 w-7 text-accent-blue" />
                  <h3 className="font-heading font-bold text-2xl md:text-3xl mt-6 tracking-tight">
                    {c.title}
                  </h3>
                  <p className="text-zinc-400 mt-4 leading-relaxed">{c.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Client examples */}
      <section
        data-testid="reach-client-examples"
        className="bg-black border-y border-zinc-800 py-20 md:py-28"
      >
        <div className="mx-auto max-w-[1400px] px-5 md:px-10">
          <div className="flex items-end justify-between flex-wrap gap-6">
            <div>
              <div className="flex items-center gap-3">
                <span className="h-px w-12 bg-accent-blue" />
                <span className="label-eyebrow text-accent-blue">
                  Clients We&apos;ve Amplified
                </span>
              </div>
              <h2 className="font-heading font-black text-4xl md:text-5xl mt-4 leading-tight tracking-tighter max-w-2xl">
                Real distribution. Real outcomes.
              </h2>
            </div>
          </div>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-5">
            {CASE_STUDIES.slice(0, 3).map((c, i) => (
              <Link
                key={c.slug}
                to={`/case-studies/${c.slug}`}
                data-testid={`reach-case-${i}`}
                className="group relative aspect-[4/5] rounded-2xl overflow-hidden bg-zinc-900 border border-white/10 hover:border-accent-blue transition-all"
              >
                {c.thumb && (
                  <img
                    src={c.thumb}
                    alt={c.cardTitle}
                    loading="lazy"
                    className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                )}
                {/* Bottom-up gradient for legibility */}
                <div
                  aria-hidden
                  className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/50 to-slate-950/10"
                />
                {/* Top badge */}
                <div className="absolute top-5 left-5 right-5 flex items-center justify-between">
                  <span className="font-mono text-[10px] text-accent-blue uppercase tracking-widest bg-slate-950/70 backdrop-blur border border-accent-blue/30 px-2.5 py-1 rounded-full">
                    Case {String(i + 1).padStart(2, "0")}
                  </span>
                  <ArrowUpRight className="h-5 w-5 text-white/70 group-hover:text-accent-blue group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all" />
                </div>
                {/* Overlay text at bottom */}
                <div className="absolute bottom-0 left-0 right-0 p-6 md:p-7">
                  <span className="block text-[10px] font-heading font-semibold uppercase tracking-widest text-accent-blue">
                    {c.client}
                  </span>
                  <p className="mt-2 font-heading font-black text-2xl md:text-3xl leading-[1.05] tracking-tight text-white">
                    {c.cardTitle}
                  </p>
                  <p className="mt-3 text-[11px] font-heading font-semibold uppercase tracking-widest text-white/50">
                    {c.cardTagline}
                  </p>
                </div>
              </Link>
            ))}
          </div>

          <div className="mt-10 flex justify-center">
            <Link
              to="/case-studies"
              data-testid="reach-view-all-cases"
              className="inline-flex items-center gap-2 rounded-full border border-white/25 hover:border-accent-blue hover:text-accent-blue text-white px-7 py-3.5 text-sm font-heading font-semibold uppercase tracking-widest transition-colors"
            >
              View all case studies <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 md:py-28">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10 text-center">
          <h2 className="font-heading font-black text-4xl md:text-6xl leading-[0.95] tracking-tighter max-w-3xl mx-auto">
            That&apos;s the difference.
          </h2>
          <p className="mt-6 text-zinc-400 max-w-xl mx-auto">
            Most agencies stop at creating content. We start there, and then
            we make sure people actually see it.
          </p>
          <Link
            to="/positioning-review"
            className="mt-10 inline-flex items-center gap-2 bg-primary-blue text-white px-7 py-4 text-sm font-bold uppercase tracking-widest hover:glow-blue transition-all"
          >
            Book A Discovery Call <ArrowUpRight className="h-4 w-4" />
          </Link>
        </div>
      </section>
    </main>
  );
}
